import { invokeWithSingleContextHeal } from "@openmeld/adapter-core";
import { isLikelyClaudeContextLostError } from "../context/claude-context-heal.js";
import { invokeClaudeAgentSdkTransport, } from "../transport/claude-agent-sdk-transport.js";
import { invokeClaudeCliTransport } from "../transport/claude-cli-transport.js";
export class ClaudeRuntimeError extends Error {
    code;
    errorType;
    originalError;
    providerAccessEvidence;
    telemetry;
    constructor(input) {
        super(input.message);
        this.name = "ClaudeRuntimeError";
        this.code = input.code;
        this.errorType = input.errorType;
        if (input.originalError) {
            this.originalError = input.originalError;
        }
        if (input.providerAccessEvidence) {
            this.providerAccessEvidence = input.providerAccessEvidence;
        }
        if (input.telemetry) {
            this.telemetry = input.telemetry;
        }
    }
}
export async function invokeClaudeWithSingleContextHeal(input) {
    const result = await invokeWithSingleContextHeal({
        contextId: input.sessionId,
        contextLossPolicy: input.executionContext || input.executionMode === "repair"
            ? "fail"
            : "rebuild",
        createRebuildFailedError: (error) => {
            const providerAccessEvidence = getClaudeProviderAccessEvidence(error);
            const originalError = getClaudeOriginalError(error);
            const telemetry = getClaudeTelemetry(error);
            return new ClaudeRuntimeError({
                code: "context.rebuild_failed",
                errorType: "adapter",
                message: `context.rebuild_failed: ${toErrorMessage(error)}`,
                ...(originalError ? { originalError } : {}),
                ...(providerAccessEvidence ? { providerAccessEvidence } : {}),
                ...(telemetry ? { telemetry } : {}),
            });
        },
        invokeOnce: (sessionId) => invokeClaudeOnce({
            executionContext: input.executionContext,
            agentControllerPermissionMode: input.agentControllerPermissionMode,
            launchContract: input.launchContract,
            claudeTimeoutMs: input.claudeTimeoutMs,
            claudeTransport: input.claudeTransport,
            claudeWorkdir: input.claudeWorkdir,
            conversationPolicy: input.conversationPolicy,
            ...(input.createAgentSdkQuery
                ? { createAgentSdkQuery: input.createAgentSdkQuery }
                : {}),
            env: input.env,
            executionMode: input.executionMode,
            instructionPrompt: input.instructionPrompt,
            ...(input.onInteractionRequest
                ? { onInteractionRequest: input.onInteractionRequest }
                : {}),
            ...(input.onProcessControlEvent
                ? { onProcessControlEvent: input.onProcessControlEvent }
                : {}),
            ...(input.onPreview ? { onPreview: input.onPreview } : {}),
            ...(input.onRuntimeEvent
                ? { onRuntimeEvent: input.onRuntimeEvent }
                : {}),
            prompt: input.prompt,
            ...(input.registerAgentSdkQuery
                ? { registerAgentSdkQuery: input.registerAgentSdkQuery }
                : {}),
            sessionId,
            streamingMode: input.streamingMode,
            taskId: input.taskId,
        }).then((value) => ({
            contextId: value.sessionId,
            output: value.output,
            ...(value.telemetry ? { telemetry: value.telemetry } : {}),
        })),
        isContextLostError: (error) => isLikelyClaudeContextLostError(error instanceof Error ? error.message : String(error)),
        onContextLost: input.onContextLost,
    });
    return {
        contextHealed: result.contextHealed,
        output: result.output,
        sessionId: result.contextId,
        ...(result.telemetry ? { telemetry: result.telemetry } : {}),
    };
}
async function invokeClaudeOnce(input) {
    try {
        if ((input.claudeTransport ?? "agent-sdk") === "cli") {
            if (input.executionContext) {
                throw new Error("execution_context.transport_unsupported");
            }
            return await invokeClaudeOnceWithCli(input);
        }
        return await invokeClaudeOnceWithAgentSdk(input);
    }
    catch (error) {
        if (error instanceof ClaudeRuntimeError) {
            throw error;
        }
        if (error &&
            typeof error === "object" &&
            "code" in error &&
            typeof error.code === "string" &&
            "errorType" in error &&
            typeof error.errorType === "string" &&
            error instanceof Error) {
            const providerAccessEvidence = getClaudeProviderAccessEvidence(error);
            const originalError = getClaudeOriginalError(error);
            const telemetry = getClaudeTelemetry(error);
            throw new ClaudeRuntimeError({
                code: error.code,
                errorType: error.errorType,
                message: error.message,
                ...(originalError ? { originalError } : {}),
                ...(providerAccessEvidence ? { providerAccessEvidence } : {}),
                ...(telemetry ? { telemetry } : {}),
            });
        }
        throw new ClaudeRuntimeError({
            code: "upstream_unreachable",
            errorType: "adapter",
            message: `upstream_unreachable: ${toErrorMessage(error)}`,
        });
    }
}
function invokeClaudeOnceWithCli(input) {
    return invokeClaudeCliTransport({
        agentControllerPermissionMode: input.agentControllerPermissionMode,
        launchContract: input.launchContract,
        claudeTimeoutMs: input.claudeTimeoutMs,
        claudeWorkdir: input.claudeWorkdir,
        conversationPolicy: input.conversationPolicy,
        env: input.env,
        executionMode: input.executionMode,
        ...(input.onProcessControlEvent
            ? { onProcessControlEvent: input.onProcessControlEvent }
            : {}),
        ...(input.onPreview ? { onPreview: input.onPreview } : {}),
        ...(input.onRuntimeEvent ? { onRuntimeEvent: input.onRuntimeEvent } : {}),
        prompt: input.prompt,
        sessionId: input.sessionId,
        streamingMode: input.streamingMode,
    });
}
function invokeClaudeOnceWithAgentSdk(input) {
    return invokeClaudeAgentSdkTransport({
        executionContext: input.executionContext,
        agentControllerPermissionMode: input.agentControllerPermissionMode,
        launchContract: input.launchContract,
        claudeTimeoutMs: input.claudeTimeoutMs,
        claudeWorkdir: input.claudeWorkdir,
        conversationPolicy: input.conversationPolicy,
        ...(input.createAgentSdkQuery
            ? { createQuery: input.createAgentSdkQuery }
            : {}),
        env: input.env,
        executionMode: input.executionMode,
        instructionPrompt: input.instructionPrompt,
        ...(input.onInteractionRequest
            ? { onInteractionRequest: input.onInteractionRequest }
            : {}),
        ...(input.onPreview ? { onPreview: input.onPreview } : {}),
        ...(input.onRuntimeEvent ? { onRuntimeEvent: input.onRuntimeEvent } : {}),
        prompt: input.prompt,
        ...(input.registerAgentSdkQuery
            ? { registerQuery: input.registerAgentSdkQuery }
            : {}),
        sessionId: input.sessionId,
        streamingMode: input.streamingMode,
        taskId: input.taskId,
    });
}
function getClaudeProviderAccessEvidence(error) {
    if (!error || typeof error !== "object") {
        return null;
    }
    const evidence = error
        .providerAccessEvidence;
    if (!evidence || typeof evidence !== "object") {
        return null;
    }
    return evidence;
}
function getClaudeOriginalError(error) {
    if (!(error && typeof error === "object")) {
        return null;
    }
    const originalError = error.originalError;
    return typeof originalError === "string" ? originalError : null;
}
function getClaudeTelemetry(error) {
    if (!(error && typeof error === "object" && "telemetry" in error)) {
        return null;
    }
    const telemetry = error.telemetry;
    return telemetry && typeof telemetry === "object"
        ? telemetry
        : null;
}
function toErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    return String(error);
}
